import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { Button, IconButton, Popover } from '@mui/material';
import { Box } from '@mui/system';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BUTTON_VARIANTS, DEFAULT_URL, EXAMPLE_URL, URL_ATTRIBUTES } from '../../constants/constants';
import { FormContext } from '../../form/FormContext';
import FormWidget from '../../form/FormWidget';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { H, V } from '../../Layout';

type LinkAttachProps = {
    disabled?: boolean;
    editor: LexicalEditor;
};

const LinkAttach = ({ disabled = false, editor }: LinkAttachProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [linkUrl, setLinkUrl] = useState(DEFAULT_URL);
    const [fromIcon, setFromIcon] = useState(false);
    const linkInputRef = useRef<HTMLInputElement | null>(null);
    const rangeRef = useRef<Range | null>(null);
    const open = Boolean(anchorEl);

    const { state } = useContext(FormContext);

    const handleOpen = (event: React.MouseEvent<HTMLElement>, link?: string, openedFromIcon: boolean = false) => {
        const selection = window.getSelection();
        event.preventDefault();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        rangeRef.current = range ? range.cloneRange() : null;
        setAnchorEl(event.currentTarget);
        setFromIcon(openedFromIcon);
        if (link) setLinkUrl(link);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setLinkUrl(DEFAULT_URL);
    };
    const handleApply = () => {
        editor.focus();

        const inputValue = linkInputRef.current?.value?.trim() || '';
        if (!inputValue) return;

        const formattedUrl = inputValue.startsWith('http') ? inputValue : `${DEFAULT_URL}${inputValue}`;
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchorNode = selection.anchor.getNode();
                const linkText = anchorNode.getTextContent();
                if (linkText.length > 0) {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
                        url: formattedUrl,
                        target: URL_ATTRIBUTES.target,
                        rel: URL_ATTRIBUTES.rel
                    });
                } else {
                    selection.insertText(inputValue);
                }
            }
        });

        handleClose();
    };

    useEffect(() => {
        if (!editor) return;

        const myListener = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;

            const maybeLink = target.closest('a') as HTMLAnchorElement | null;
            if (!maybeLink) return;

            handleOpen(event as unknown as React.MouseEvent<HTMLElement>, maybeLink.href, true);
        };

        const unregister = editor.registerRootListener((rootElement, prevRootElement) => {
            rootElement?.addEventListener('click', myListener);
            if (prevRootElement) {
                prevRootElement.removeEventListener('click', myListener);
            }
        });

        return unregister;
    }, [editor]);

    const handleChange = useCallback(() => { }, []);

    return (
        <>
            <IconButton
                sx={{
                    borderRadius: 0,
                    '&:hover': { backgroundColor: 'action.hover' }
                }}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOpen}
            >
                <Icons.Link />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={
                    fromIcon ? { vertical: 'top', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'right' }
                }
                transformOrigin={
                    fromIcon ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' }
                }
            >
                <V gap={1} sx={{ padding: 1, width: 220 }}>
                    <Box
                        component="form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleApply();
                        }}
                    >
                        <FormWidget
                            ref={linkInputRef}
                            size="small"
                            autoFocus
                            placeholder={EXAMPLE_URL}
                            value={linkUrl}
                            onChange={state ? () => undefined : handleChange} // Provide onChange when state is not available to prevent console errors when formProvider state and an explicit onChange are both absent.
                        />
                    </Box>

                    <H gap={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={handleClose}>
                            <I18n token={'dialog.cancel'} />
                        </Button>
                        <Button size="small" variant={BUTTON_VARIANTS.contained} onClick={handleApply}>
                            <I18n token={'dialog.save'} />
                        </Button>
                    </H>
                </V>
            </Popover>
        </>
    );
};

export default LinkAttach;
